import BookingData from "../classes/BookingData";
import Accomodation, { IAccomodation } from "../model/Accomodation";
import { IInsurance } from "../model/Insurance/Insurance";
import { IService } from "../model/Service/Service";
import { BookingQuotePrestation, PaymentMethodEnum, QuoteState } from "../../../requests-module";
import AccomodationService from "../model/Service/AccomodationService";
import ParticipantService from "../model/Service/ParticipantService";
import { ITraveller } from "../model/Traveller";
import ParticipantInsurance from "../model/Insurance/ParticipantInsurance";
import GlobalInsurance from "../model/Insurance/GlobalInsurance";

const alertDeveloper = (message: string) => {
    if (window.env.VUE_APP_ENV && window.env.VUE_APP_ENV !== 'prod') {
        alert(message)
    }
    console.error(message)
}

class ECommerceItem {
    item_LiheId = ""
    item_LiheName = ""
    item_PrestId = ""
    item_PrestName = ""
    item_price_ati = NaN
    item_quantity = NaN
    item_category = ""
    item_category2 = ""
    item_category3 = ""
    item_category4 = ""
    item_variant = ""
    item_ville_name = ""
    item_ville_id = ""
    item_region_name = ""
    item_region_id = ""
    item_pays_name = ""
    item_pays_id = ""
    item_begin_date = ""
    item_end_date = ""
    item_duration = ""
    item_reviews_note = ""
    item_reviews = ""
}

class AddRemoveEvent {
    event: "add_to_cart_push_cart" | "remove_from_cart"
    ecommerce?: {
        items: ECommerceItem[]
    };
    constructor(
        event: "add_to_cart_push_cart" | "remove_from_cart",
        ecommerce?: {
            items: ECommerceItem[]
        },
    ) {
        this.event = event
        this.ecommerce = ecommerce
    }
}
class BaseEvent {
    event: EventType
    cart_id = "";
    cart_amount_ati = NaN;
    cart_amount_ati_without_fees = NaN;
    fees = NaN;
    promocode = "";
    ecommerce?: {
        items: ECommerceItem[];
    };
    constructor(
        event: EventType,
        cart_id = "",
        cart_amount_ati = NaN,
        cart_amount_ati_without_fees = NaN,
        fees = NaN,
        promocode = "",
        ecommerce?: {
            items: ECommerceItem[];
        }
    ) {
        this.event = event
        this.cart_id = cart_id
        this.cart_amount_ati = cart_amount_ati
        this.cart_amount_ati_without_fees = cart_amount_ati_without_fees
        this.fees = fees
        this.promocode = promocode
        this.ecommerce = ecommerce
    }
    validate() {
        if (!this.event) alertDeveloper("TRACKING DATA ERROR: event is mandatory")
    }
}

class TransactionEvent extends BaseEvent {
    transaction_id?: string;
    deposit_amount_ati?: number;
    cart_discount_amount_ati?: number;
    payment_method?: PaymentMethodEnum;
    override validate(): void {
        if (typeof this.deposit_amount_ati === "undefined") alertDeveloper("TRACKING DATA ERROR: deposit_amount_ati is mandatory")
        if (typeof this.cart_discount_amount_ati === "undefined") alertDeveloper("TRACKING DATA ERROR: cart_discount_amount_ati is mandatory")
    }
}


type EventType = "account_creation" |
    "begin_checkout" |
    "user_infos" |
    "payment" |
    "purchase" |
    "quote_creation" |
    "add_to_cart_push_cart" |
    "remove_from_cart" |
    "age_change_cart_reset" |
    "product_unavailable" |
    "overbooking" |
    "payment_error" |
    "cart_update" |
    "login_step1" |
    "login_step2"

class AccountCreationEvent extends BaseEvent {
    optin_newsletter?: string;
    constructor(
        ...args: any[]
    ) {
        super("account_creation", ...args);
    }
    override validate(): void {
        if (typeof this.optin_newsletter === "undefined") alertDeveloper("TRACKING DATA ERROR: optin_newsletter is mandatory")
    }
}

class BeginCheckoutEvent extends BaseEvent {
    constructor(
        ...args: any[]
    ) {
        super("begin_checkout", ...args);
    }
}

class UserInfosEvent extends BaseEvent {
    constructor(
        ...args: any[]
    ) {
        super("user_infos", ...args);
    }
}

class PaymentEvent extends BaseEvent {
    constructor(
        ...args: any[]
    ) {
        super("payment", ...args);
    }
}

class PurchaseEvent extends BaseEvent {
    transaction_id?: string;
    constructor(
        ...args: any[]
    ) {
        super("purchase", ...args);
    }
}

class QuoteCreationEvent extends BaseEvent {
    quote_id?: string;
    constructor(
        ...args: any[]
    ) {
        super("quote_creation", ...args);
    }
    override validate(): void {
        if (!this.quote_id) throw new Error("TRACKING DATA ERROR: quote_id is mandatory")
    }
}
class AgeChangeCartResetEvent extends BaseEvent {
    constructor(
        ...args: any[]
    ) {
        super("age_change_cart_reset", ...args);
    }
}
class ProductUnavailableEvent extends BaseEvent {
    constructor(
        ...args: any[]
    ) {
        super("product_unavailable", ...args);
    }
}
class OverbookingEvent extends TransactionEvent {
    constructor(
        ...args: any[]
    ) {
        super("overbooking", ...args);
    }
}
class PaymentErrorEvent extends TransactionEvent {
    constructor(
        ...args: any[]
    ) {
        super("payment_error", ...args);
    }
}

class AddToCartPushCartEvent extends BaseEvent {
    constructor(
        ...args: any[]
    ) {
        super("add_to_cart_push_cart", ...args);
    }
}

class RemoveFromCartEvent extends BaseEvent {
    constructor(
        ...args: any[]
    ) {
        super("remove_from_cart", ...args);
    }
}
class UpdateCartEvent extends BaseEvent {
    constructor(
        ...args: any[]
    ) {
        super("cart_update", ...args);
    }
}
class LoginStep1Event extends BaseEvent {
    constructor(
        ...args: any[]
    ) {
        super("login_step1", ...args);
    }
}
class LoginStep2Event extends BaseEvent {
    constructor(
        ...args: any[]
    ) {
        super("login_step2", ...args);
    }
}



type TrackingEventConstructor = {
    new(...args: any[]): BaseEvent;
};

type TrackingEventType<T> = T extends typeof AccountCreationEvent
    ? AccountCreationEvent
    : T extends typeof BeginCheckoutEvent
    ? BeginCheckoutEvent
    : T extends typeof UserInfosEvent
    ? UserInfosEvent
    : T extends typeof PaymentEvent
    ? PaymentEvent
    : T extends typeof PurchaseEvent
    ? PurchaseEvent
    : T extends typeof QuoteCreationEvent
    ? QuoteCreationEvent
    : T extends typeof OverbookingEvent
    ? OverbookingEvent
    : T extends typeof ProductUnavailableEvent
    ? ProductUnavailableEvent
    : T extends typeof PaymentErrorEvent
    ? PaymentErrorEvent
    : T extends typeof AddToCartPushCartEvent
    ? AddToCartPushCartEvent
    : T extends typeof RemoveFromCartEvent
    ? RemoveFromCartEvent
    : T extends typeof UpdateCartEvent
    ? UpdateCartEvent
    : T extends typeof LoginStep1Event
    ? LoginStep1Event
    : T extends typeof LoginStep2Event
    ? LoginStep2Event
    : never;

type CommonBookingTrackingEventParams = [
    cart_id?: string,
    cart_amount_ati?: string,
    cart_amount_ati_without_fees?: string,
    fees?: string,
    promocode?: string,
    ecommerce?: { items: ECommerceItem[] }
] | any[]

const getCommonBookingTrackingEventParams = (bookingData: BookingData, source: "state" | "booking"): CommonBookingTrackingEventParams => {
    const dataSource: QuoteState | null | undefined = source === "state" ? bookingData.responses.bookingQuoteResponse?.state : bookingData.responses.bookingQuoteResponse?.booking

    const cart_id: string = bookingData.responses.bookingQuoteResponse?.boReference || ""
    const cart_amount_ati = Number(dataSource?.totalPrice)

    let fees = 0
    bookingData.feesArr.forEach((fee) => {
        fees += fee.getValue()
    })

    const cart_amount_ati_without_fees: number = cart_amount_ati ? cart_amount_ati - fees : NaN

    const promocode: string = dataSource?.promocode || ""

    const selectedAccomodations: IAccomodation[] = bookingData.lihe.getAccomodations()
    const selectedInsurances: IInsurance[] = Array.from(bookingData.selectedInsurancesMap.values())
    const selectedServices: IService[] = Array.from(bookingData.selectedServicesMap.values())

    const everythingSelected: (IAccomodation | IInsurance | IService)[] = [...selectedAccomodations, ...selectedInsurances, ...selectedServices]
    const items: ECommerceItem[] | undefined = everythingSelected.map((item: IAccomodation | IInsurance | IService) => {
        return buildECommerceItem(item, bookingData)
    })
    const ecommerce: {
        items: ECommerceItem[];
    } = { items }

    return [
        cart_id,
        cart_amount_ati / 100,
        cart_amount_ati_without_fees / 100,
        fees / 100,
        promocode,
        ecommerce,
    ]
}


const buildECommerceItem = (
    item: IService | IInsurance | IAccomodation,
    bookingData: BookingData,
): ECommerceItem => {
    const anyTraveller: ITraveller = bookingData.travellersArr[0]
    const anyTravellerId: string = anyTraveller.getId()

    const ecommerceItem = new ECommerceItem()
    ecommerceItem.item_LiheId = `${bookingData.lihe.getContent()?.getLiheId()}` || ""
    ecommerceItem.item_LiheName = bookingData.lihe.getContent()?.getLiheName() || ""
    ecommerceItem.item_quantity = Number(item.getQuantity())
    ecommerceItem.item_category = bookingData.lihe.getContent()?.getRegionName() || ""
    // ecommerceItem.item_category2 = // TODO
    // ecommerceItem.item_category3 =
    // ecommerceItem.item_category4 =
    // ecommerceItem.item_variant = // TODO pack code
    ecommerceItem.item_ville_name = bookingData.lihe.getContent()?.getStationName() || ""
    ecommerceItem.item_ville_id = bookingData.lihe.getContent()?.getStationId() || ""
    ecommerceItem.item_region_name = bookingData.lihe.getContent()?.getRegionName() || ""
    ecommerceItem.item_region_id = bookingData.lihe.getContent()?.getRegionId() || ""
    ecommerceItem.item_pays_name = bookingData.lihe.getContent()?.getCountryName() || ""
    ecommerceItem.item_pays_id = bookingData.lihe.getContent()?.getCountryId() || ""
    ecommerceItem.item_begin_date = item.getStartDate() || ""
    ecommerceItem.item_end_date = item.getEndDate() || ""
    ecommerceItem.item_duration = `${item.getDuration()}`
    ecommerceItem.item_reviews_note = ""
    ecommerceItem.item_reviews = ""

    if (item instanceof AccomodationService) {
        ecommerceItem.item_PrestId = item.getIdFull()
        ecommerceItem.item_PrestName = item.getServiceContent()?.getName() || ""
        ecommerceItem.item_price_ati = item.getBaseServiceQuoteObject()?.totalPrice || item.getBaseServiceQuoteObject()?.price || NaN
    } else if (item instanceof ParticipantService) {
        ecommerceItem.item_PrestId = item.getIdFull()
        ecommerceItem.item_PrestName = item.getServiceContent()?.getName() || ""
        ecommerceItem.item_price_ati = item.getPriceForTravellerId(anyTravellerId) || NaN
    } else if (item instanceof ParticipantInsurance) {
        ecommerceItem.item_quantity = Number(item.getBaseInsuranceQuoteObject()?.quantity)
        ecommerceItem.item_PrestId = item.getIdFull()
        ecommerceItem.item_PrestName = item.getInsuranceContent()?.getName() || ""
        ecommerceItem.item_price_ati = item.getTotalPriceByTravellerOrAccomodationId(anyTravellerId) || NaN
    } else if (item instanceof GlobalInsurance) {
        ecommerceItem.item_PrestId = item.getIdFull()
        ecommerceItem.item_PrestName = item.getInsuranceContent()?.getName() || ""
        ecommerceItem.item_price_ati = item.getTotalPrice() || NaN
    } else if (item instanceof Accomodation) {
        ecommerceItem.item_PrestId = item.getIdFull()
        ecommerceItem.item_PrestName = item.getAccomodationContent()?.getAccomodationName() || ""
        ecommerceItem.item_price_ati = item.getBaseAccomodationQuoteObject()?.amount || NaN
    } else throw new Error("TRACKING DATA ERROR: can only process Service, Insurance and Accomodation")

    // add point to price, because the numbers are in cents, e.g. $10 is 1000, so need to divide by 100. This is the request of the tracking analyst
    if (ecommerceItem.item_price_ati) ecommerceItem.item_price_ati = ecommerceItem.item_price_ati / 100

    return ecommerceItem

}

class BookingTrackingEventFactory {

    static buildBookingTrackingEvent<T extends TrackingEventConstructor>(
        cls: T,
        commonParams: CommonBookingTrackingEventParams,
        additionalParams: { [key: string]: any }
    ): any {
        let res: any
        switch (cls) {
            case (AccountCreationEvent): {
                res = new AccountCreationEvent(...commonParams) as TrackingEventType<T>;
                res.optin_newsletter = additionalParams.optin_newsletter
                break
            }
            case (BeginCheckoutEvent): {
                res = new BeginCheckoutEvent(...commonParams) as TrackingEventType<T>;
                break
            }
            case (UserInfosEvent): {
                res = new UserInfosEvent(...commonParams) as TrackingEventType<T>;
                break
            }
            case (ProductUnavailableEvent): {
                res = new ProductUnavailableEvent(...commonParams) as TrackingEventType<T>;
                break
            }
            case (PaymentEvent): {
                res = new PaymentEvent(...commonParams) as TrackingEventType<T>;
                break
            }
            case (PurchaseEvent): {
                res = new PurchaseEvent(...commonParams) as TrackingEventType<T>;
                res.transaction_id = additionalParams.transaction_id
                res.deposit_amount_ati = additionalParams.deposit_amount_ati
                res.cart_discount_amount_ati = additionalParams.cart_discount_amount_ati
                res.payment_method = additionalParams.payment_method
                break
            }
            case (QuoteCreationEvent): {
                res = new QuoteCreationEvent(...commonParams) as TrackingEventType<T>;
                res.quote_id = additionalParams.quote_id
                break
            }
            case (AgeChangeCartResetEvent): {
                res = new AgeChangeCartResetEvent(...commonParams) as TrackingEventType<T>;
                break
            }
            case (OverbookingEvent): {
                res = new OverbookingEvent(...commonParams) as TrackingEventType<T>;
                res.transaction_id = additionalParams.transaction_id
                res.deposit_amount_ati = additionalParams.deposit_amount_ati
                res.cart_discount_amount_ati = additionalParams.cart_discount_amount_ati
                res.payment_method = additionalParams.payment_method
                break
            }
            case (PaymentErrorEvent): {
                res = new PaymentErrorEvent(...commonParams) as TrackingEventType<T>;
                res.transaction_id = additionalParams.transaction_id
                res.deposit_amount_ati = additionalParams.deposit_amount_ati
                res.cart_discount_amount_ati = additionalParams.cart_discount_amount_ati
                res.payment_method = additionalParams.payment_method
                break
            }
            case (UpdateCartEvent): {
                res = new UpdateCartEvent(...commonParams) as TrackingEventType<T>;
                break
            }
            case (LoginStep1Event): {
                res = new LoginStep1Event(...commonParams) as TrackingEventType<T>;
                break
            }
            case (LoginStep2Event): {
                res = new LoginStep2Event(...commonParams) as TrackingEventType<T>;
                break
            }
            default: {
                throw new Error("Unknown event class");
            }
        }
        res.validate()
        return res
    }
}


export const getAddRemoveEvent = (
    action: "add" | "remove",
    items: (IService | IInsurance | IAccomodation)[],
    bookingData: BookingData,
): AddRemoveEvent => {
    const event = action === "add" ? "add_to_cart_push_cart" : "remove_from_cart"
    const eCommerceItems = items.map((item) => {
        return buildECommerceItem(item, bookingData)
    })
    const obj = new AddRemoveEvent(event, { items: eCommerceItems })
    return obj
}



export const getBookingTrackingEvent = <T extends TrackingEventConstructor>(
    cls: T,
    additionalParams: { [key: string]: any },
    bookingData: BookingData,
    source: "state" | "booking",
) => {
    const commonParams = getCommonBookingTrackingEventParams(bookingData, source)
    return BookingTrackingEventFactory.buildBookingTrackingEvent(cls, commonParams, additionalParams)
}

class Tracking {
    static trackAccountCreation(bookingData: BookingData, optin_newsletter: boolean) {
        try {
            const trackingEvent = getBookingTrackingEvent(AccountCreationEvent, { optin_newsletter }, bookingData, "state")
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackBeginCheckout(bookingData: BookingData) {
        try {
            const trackingEvent = getBookingTrackingEvent(BeginCheckoutEvent, {}, bookingData, "state")
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackUserInfos(bookingData: BookingData) {
        try {
            const trackingEvent = getBookingTrackingEvent(UserInfosEvent, {}, bookingData, "state")
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackPayment(
        bookingData: BookingData,
    ) {
        try {
            const trackingEvent = getBookingTrackingEvent(PaymentEvent, {}, bookingData, "state")
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackPurchase(
        bookingData: BookingData,
        transaction_id: string,
        deposit_amount_ati: number,
        cart_discount_amount_ati: number,
        payment_method: PaymentMethodEnum | undefined,
    ) {
        try {
            const trackingEvent = getBookingTrackingEvent(PurchaseEvent, { transaction_id, deposit_amount_ati, cart_discount_amount_ati, payment_method }, bookingData, "state")
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackQuoteCreation(bookingData: BookingData, quote_id: string) {
        try {
            const trackingEvent = getBookingTrackingEvent(QuoteCreationEvent, { quote_id }, bookingData, "state")
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackAgeChangeCartReset(bookingData: BookingData) {
        try {
            const trackingEvent = getBookingTrackingEvent(AgeChangeCartResetEvent, {}, bookingData, "state")
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackOverbooking(
        bookingData: BookingData,
        transaction_id: string,
        deposit_amount_ati: number,
        cart_discount_amount_ati: number,
        payment_method?: PaymentMethodEnum,
    ) {
        try {
            const trackingEvent = getBookingTrackingEvent(OverbookingEvent, { transaction_id, deposit_amount_ati, cart_discount_amount_ati, payment_method }, bookingData, "state")
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackProductUnavailable(
        lihe_id: string,
        start_date: string,
        end_date: string,
        prestations: BookingQuotePrestation[],
        season: string, // at the moment the season is not used in tracking, but obviously it should, so I will keep it as an argument
    ) {
        try {
            const cart_id = ""
            const cart_amount_ati = NaN
            const cart_amount_ati_without_fees = NaN
            const fees = NaN
            const promocode = ""
            const ecommerce: ECommerceItem[] = []
            prestations.forEach((prest: BookingQuotePrestation) => {
                const ecommerceItem = new ECommerceItem()
                ecommerceItem.item_LiheId = lihe_id
                ecommerceItem.item_PrestId = prest.id
                ecommerceItem.item_quantity = Number(prest.quantity)
                ecommerceItem.item_begin_date = start_date
                ecommerceItem.item_end_date = end_date
                ecommerce.push(ecommerceItem)
            })
            const trackingEvent: ProductUnavailableEvent = new ProductUnavailableEvent(
                cart_id,
                cart_amount_ati,
                cart_amount_ati_without_fees,
                fees,
                promocode,
                ecommerce,
            )
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackPaymentError(
        bookingData: BookingData,
        transaction_id: string,
        deposit_amount_ati: number,
        cart_discount_amount_ati: number,
        payment_method?: PaymentMethodEnum,
    ) {
        try {
            const trackingEvent = getBookingTrackingEvent(PaymentErrorEvent, { transaction_id, deposit_amount_ati, cart_discount_amount_ati, payment_method }, bookingData, "state")
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackAddItems(bookingData: BookingData, items: (IService | IInsurance | IAccomodation)[]) {
        try {
            const trackingEvent = getAddRemoveEvent("add", items, bookingData)
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackRemoveItems(bookingData: BookingData, items: (IService | IInsurance | IAccomodation)[]) {
        try {
            const trackingEvent = getAddRemoveEvent("remove", items, bookingData)
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackUpdateCart(
        bookingData: BookingData,
    ) {
        try {
            const trackingEvent = getBookingTrackingEvent(UpdateCartEvent, {}, bookingData, "state")
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackLoginStep1Cart(
        bookingData: BookingData,
    ) {
        try {
            const trackingEvent = getBookingTrackingEvent(LoginStep1Event, {}, bookingData, "state")
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }
    static trackLoginStep2Cart(
        bookingData: BookingData,
    ) {
        try {
            const trackingEvent = getBookingTrackingEvent(LoginStep2Event, {}, bookingData, "state")
            window.dataLayer.push(trackingEvent)
        } catch (err) {
            console.error("TRACKING FAILED: ", err)
        }
    }

}

export default Tracking